import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { ChartComponentProps, getBaseOption, formatTooltipValue, ECHARTS_OPTS } from './chartConfig';

const PIE_LEGEND_MAX = 20;

export const PieChart: React.FC<ChartComponentProps> = ({ chart, height = 320, colors }) => {
  const isDonut = chart.type === 'donut';

  const option = useMemo((): EChartsOption => {
    const data = chart.data || [];
    if (data.length === 0) return {};

    const pieData = data.map(d => ({ name: String(d.name ?? ''), value: d.value ?? 0 }));
    const legendTruncated = data.length > PIE_LEGEND_MAX;
    const legendData = legendTruncated
      ? [...pieData.slice(0, PIE_LEGEND_MAX).map(d => d.name), '...']
      : undefined;

    const base = getBaseOption(colors);

    return {
      ...base,
      tooltip: {
        ...(base.tooltip as object),
        trigger: 'item',
        formatter: (params: unknown) => {
          const p = params as { name?: string; value?: number; percent?: number; marker?: string };
          return `<div style="font-size:12px;color:#F7F3EE">${p.marker}<span>${p.name}</span><br/><span>${formatTooltipValue(p.value)}</span> <span style="color:#D9CFC4">(${p.percent?.toFixed(1)}%)</span></div>`;
        },
      },
      legend: {
        ...base.legend,
        orient: 'vertical' as const,
        right: 10,
        top: 'center' as const,
        textStyle: { fontSize: 11 },
        data: legendData,
      },
      series: [
        {
          type: 'pie',
          radius: isDonut ? ['45%', '70%'] : ['0%', '70%'],
          center: ['40%', '50%'],
          data: pieData,
          padAngle: 2,
          itemStyle: { borderRadius: 4 },
          label: {
            show: data.length <= 8,
            formatter: '{b}: {d}%',
            fontSize: 11,
          },
          emphasis: {
            itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.15)' },
          },
        },
      ],
    };
  }, [chart, colors, isDonut]);

  return <ReactECharts option={option} style={{ height }} notMerge lazyUpdate opts={ECHARTS_OPTS} />;
};
